/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable guard-for-in */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useEffect, useState } from "react";
import { FabricObject, SelectedOption } from "../../../../types";
import CustomizableCanvas from "../CustomizableCanvas";
import useCustomizableContext from "~/CustomizableToolPage/useCustomizableContext";
import {
  MAX_TEXT_BOX_ALLOWED_PER_CANVAS,
  TEXT_BOX_PLACEHOLDER_TEXT,
} from "~/CustomizableToolPage/CustomizableTool/constants";
import {
  addText,
  deleteByClientIdentifiers,
  getTextBoxes,
} from "~/CustomizableToolPage/fabric/utils";
import TextControls from "./TextControls";
import { User } from "~/common/Store/connectUserAndChat";

interface Props {
  user: User;
  toggleNavigationSidebar: () => void;
}

const TextPanel = ({ user, toggleNavigationSidebar }: Props) => {
  const {
    // @ts-ignore
    selectedRegion,
    // @ts-ignore
    currentNavigationKey,
    // thunks
    // @ts-ignore
    selectOption,
    // selectors
    // @ts-ignore
    getNavigationKeyData,
    // @ts-ignore
    getCurrentControls,
    // @ts-ignore
    getSelectedOptions,
    // @ts-ignore
    addCustomizations,
    // @ts-ignore
    clearCustomizations,
    // Fabric
    fabric: { canvas, activeRegion },
  } = useCustomizableContext();

  const navigationKeyData = getNavigationKeyData()[
    currentNavigationKey!.currentNavigationKey
  ];

  const { actualHeight, actualWidth, regionName } = navigationKeyData.regions![
    selectedRegion
  ]!;

  const selectedOptions = getSelectedOptions() || {};
  const currentControls = getCurrentControls();

  const addPlaceholderTextBoxes = useCallback(
    (textBoxesToAdd: number) => {
      const sectionAmount = 100 / textBoxesToAdd;

      const subSection = sectionAmount / 2;
      for (let count = 0; count < textBoxesToAdd; count += 1) {
        let yPercentage = 0.5;
        if (count === 0) {
          yPercentage = subSection;
        } else {
          yPercentage = sectionAmount + count * subSection;
        }
        // This is where we decide what conditions are possible for text, (ex: number of characters, numbers only, etc...);
        const customConditions = {
          yPercentage: yPercentage / 100,
        };

        addText(canvas, activeRegion!, customConditions);
      }
    },
    [activeRegion, canvas]
  );

  // There are times when there is only 1 "number of lines" option in the data.
  // This results in no dropdown appearing, which means the user can't manually
  // add a textbox. We check for this case and add it automatically here.
  useEffect(() => {
    // @ts-ignore
    // eslint-disable-next-line no-underscore-dangle
    if (canvas.cacheCanvasEl && !canvas._objects.length && activeRegion) {
      let doWeHaveNumberOfLines = false;
      const selectedOptions = getSelectedOptions();

      Object.keys(selectedOptions).forEach((optionId) => {
        if (
          navigationKeyData!.regions![selectedRegion]!.customizations![
            optionId
          ]!.attributeName === "number of lines"
        ) {
          doWeHaveNumberOfLines = true;
          if (selectedOptions[optionId] !== 0) {
            // @ts-ignore
            const numberOfTextBoxesToAdd = navigationKeyData.regions![
              selectedRegion
            ].customizations[optionId]!.values[selectedOptions[optionId]]
              .numericValue;

            addPlaceholderTextBoxes(numberOfTextBoxesToAdd!);
          }
        }
      });
      if (!doWeHaveNumberOfLines) {
        addPlaceholderTextBoxes(MAX_TEXT_BOX_ALLOWED_PER_CANVAS);
      }
    }
  }, [
    addPlaceholderTextBoxes,
    canvas,
    getSelectedOptions,
    selectedRegion,
    navigationKeyData,
    activeRegion,
  ]);

  const updateValue = useCallback(
    (valueId: string, optionId: number) => {
      // @ts-ignore
      const payload: SelectedOption = {
        optionId: navigationKeyData.id?.toString() || "",
        attributeId: optionId.toString(),
        regionId:
          navigationKeyData.regions![selectedRegion]?.id?.toString() || "",
        attributeValueId: Number(valueId),
      };

      const customized = navigationKeyData!.regions![selectedRegion]!
        .customizations![optionId];

      const oldValueId = getSelectedOptions()[optionId]!;

      selectOption(payload);

      if (customized?.attributeName === "number of lines") {
        const oldBoxCount =
          oldValueId === 0
            ? 0
            : customized.values![oldValueId]?.numericValue || 0;
        const newBoxCount = customized.values![valueId]?.numericValue || 0!;

        if (newBoxCount < oldBoxCount) {
          const textBoxesToRemove = getTextBoxes(canvas)
            .sort(
              // @ts-ignore
              (a: FabricObject, b: FabricObject) =>
                Number(a.isPlaceholder) - Number(b.isPlaceholder)
            )
            .slice(newBoxCount);

          // dispatch new state
          deleteByClientIdentifiers(
            canvas,
            // @ts-ignore
            textBoxesToRemove.map((t) => t.clientIdentifier)
          );
        }

        if (oldBoxCount < newBoxCount) {
          addPlaceholderTextBoxes(newBoxCount - oldBoxCount);
        }
      }
    },
    [
      navigationKeyData,
      selectedRegion,
      getSelectedOptions,
      selectOption,
      canvas,
      addPlaceholderTextBoxes,
    ]
  );

  const handleTextBoxUpdated = useCallback(
    ({ canvas }: fabric.Object) => {
      const { regions } = navigationKeyData;
      // customizations is the 'possible' customizations, not what is chosen
      const { id, customizations, selectedOptions } =
        regions[selectedRegion] ?? {};

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const attributeData: any = Object.values(customizations).find(
        // @ts-ignore
        (c) => c.attributeName === "number of lines"
      );
      const optionData =
        attributeData.values[selectedOptions[attributeData.id]];

      if (!attributeData || !optionData) {
        return;
      }

      const textBoxes = getTextBoxes(canvas!).filter(
        // @ts-ignore
        (tb) => !tb.isPlaceholder && tb.text.length
      );

      if (!textBoxes.length) {
        clearCustomizations(id);
        return;
      }

      addCustomizations({
        regionId: id,
        optionId: attributeData.id,
        attributeId: attributeData.attributeId,
        attributeValueId: optionData.id,
        attributeName: attributeData.attributeName,
        // @ts-ignore
        values: textBoxes.map((tb) => tb.text),
      });
    },
    [navigationKeyData, selectedRegion, addCustomizations, clearCustomizations]
  );

  const [activeClientIdentifier, setActiveClientIdentifier] = useState("");

  const handleSelectionChange = useCallback(
    (obj: fabric.Object | null, isSelected: boolean) => {
      const newSelection =
        !!obj &&
        isSelected &&
        // @ts-ignore
        (!obj.isPlaceholder || obj.text !== TEXT_BOX_PLACEHOLDER_TEXT)
          ? // @ts-ignore
            obj.clientIdentifier
          : "";

      setActiveClientIdentifier(newSelection);
    },
    []
  );

  // @ts-ignore
  return (
    <div id="text-panel">
      <span
        className="icon-list customize__menu-icon"
        onClick={toggleNavigationSidebar}
      >
        Options
      </span>

      <div className="customize__img-content">
        {actualWidth > 0 && actualHeight > 0 && !!regionName.length && (
          <div>
            {`${regionName} - Print Area: ${actualHeight}"H X ${actualWidth}"W`}
          </div>
        )}
      </div>

      <CustomizableCanvas
        user={user}
        onObjectModified={handleTextBoxUpdated}
        onSelectionChange={handleSelectionChange}
        showUploadDropzone={false}
      />

      <div className="form">
        <div className="form__group">
          <TextControls activeClientIdentifier={activeClientIdentifier} />
        </div>

        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        {currentControls?.map((option, index) => (
          <div
            className={`${
              index + 1 >= currentControls.length ? "form__group" : ""
            } ${index + 1 < currentControls.length ? "form__split" : ""}`}
            key={option?.id}
          >
            {!(index % 2 === 1 && index + 1 <= currentControls.length) &&
              ((index + 1 < currentControls.length && (
                <>
                  <div className="form__group cols-xs--12 cols-sm--6">
                    <div className="form__control-container">
                      <select
                        className="form__control option-select"
                        value={selectedOptions[option!.id!]}
                        onChange={(e) =>
                          updateValue(e.target.value, option!.id!)
                        }
                      >
                        <option className="form__option" value="0" disabled>
                          Select an Option
                        </option>
                        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                        {/* @ts-ignore */}
                        {option.values.map((value) => (
                          <option
                            className="form__option"
                            key={value.id}
                            value={value.id}
                          >
                            {value.displayValue}
                          </option>
                        ))}
                      </select>
                      <label className="form__label">
                        {option!.attributeName}
                      </label>
                      <span className="icon-down-micro form__control-icon form__control-icon--lg" />
                    </div>
                  </div>
                  <div className="form__group cols-xs--12 cols-sm--6">
                    <div className="form__control-container">
                      <select
                        className="form__control option-select"
                        value={selectedOptions[currentControls[index + 1]!.id!]}
                        onChange={(e) =>
                          updateValue(
                            e.target.value,
                            currentControls[index + 1]!.id!
                          )
                        }
                      >
                        <option className="form__option" value="0" disabled>
                          Select an Option
                        </option>
                        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                        {/* @ts-ignore */}
                        {Object.values(
                          currentControls[index + 1]?.values || []
                        ).map((value) => (
                          <option
                            className="form__option"
                            // @ts-ignore
                            key={value.id}
                            // @ts-ignore
                            value={value.id}
                          >
                            {
                              // @ts-ignore
                              value.displayValue
                            }
                          </option>
                        ))}
                      </select>
                      <label className="form__label">
                        {currentControls[index + 1]!.attributeName}
                      </label>
                      <span className="icon-down-micro form__control-icon form__control-icon--lg" />
                    </div>
                  </div>
                </>
              )) || (
                <div className="form__control-container">
                  <select
                    className="form__control option-select"
                    value={selectedOptions[option!.id!]}
                    onChange={(e) => updateValue(e.target.value, option!.id!)}
                  >
                    <option className="form__option" value="0" disabled>
                      Select an Option
                    </option>
                    {Object.values(option!.values!).map((value) => (
                      <option
                        className="form__option"
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        key={value.id}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        value={value.id}
                      >
                        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                        {/* @ts-ignore */}
                        {value.displayValue}
                      </option>
                    ))}
                  </select>
                  <label className="form__label">{option?.attributeName}</label>
                  <span className="icon-down-micro form__control-icon form__control-icon--lg" />
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TextPanel;
